import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  TextField,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useEffect, useState } from "react";
import CustomButton from "components/button/CustomButton";
import { FormattedMessage, useIntl } from "react-intl";
import { useOutletContext } from "react-router-dom";
import { NumericString } from "types/numericString";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { formatCardNumber } from "components/card/Card";
import CardService from "services/cardService";
import { CardContext } from "../CardPageLayout";
import { resetCard, updateCard } from "app/cardsSlice";
import LoadingSpinner from "components/LoadingSpinner";
import CardLayout from "components/layout/CardLayout";

const Transfer = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { currentCard } = useOutletContext<CardContext>();
  const {
    iapCdno: cardNumber,
    // acnRmd: balance
    iapCrdStcd: status,
  } = currentCard || {};
  const isActive = status === "01";
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    const getBalance = async () => {
      const { data } = await CardService.getCardBalance(cardNumber);
      const balance = Number(data?.acnRmd || 0);
      setBalance(balance);
    };

    getBalance();
  }, [cardNumber]);

  const cards = useAppSelector((state) => state.cards);
  const toCards = cards.filter(
    (card) => card.iapCrdStcd === "01" && card.iapCdno !== cardNumber
  );
  const [toCardNo, setToCardNo] = useState(toCards[0]?.iapCdno);

  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [balanceToTransfer, setBalanceToTransfer] = useState("0");
  const numericBalanceToTransfer = Number(
    balanceToTransfer.replaceAll(",", "")
  );

  useEffect(() => {
    setToCardNo(toCards[0]?.iapCdno);
    setBalanceToTransfer("0");
  }, [currentCard]); // toCards도 dependency에 추가하면 매번 toCardNo가 초기화되서 넣지 않아야 한다.

  // TODO: 현재 balance보다 큰 경우 처리하기
  const handleChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (parseInt(value) > balance) {
      alert("Cannot transfer more than balance");
      return;
    }

    setBalanceToTransfer(`${Number(value)}`);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setBalanceToTransfer(`${numericBalanceToTransfer || "0"}`);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setBalanceToTransfer(
      numericBalanceToTransfer > 0
        ? numericBalanceToTransfer.toLocaleString()
        : "0"
    );
  };

  const handleChangeToCard = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setToCardNo(value as NumericString);
  };

  const transferBalance = async () => {
    setIsLoading(true);

    await CardService.transferBalance(
      cardNumber,
      toCardNo,
      numericBalanceToTransfer
    );

    // const newFromCardBalance = await CardService.getCardBalance(currentCardNo);
    // const newToCardBalance = await CardService.getCardBalance(toCardNo);
    // dispatch(
    //   updateCard({
    //     cardNumber: currentCardNo,
    //     balance: Number(newFromCardBalance.acnRmd),
    //   })
    // );
    // dispatch(
    //   updateCard({
    //     cardNumber: toCardNo,
    //     balance: Number(newToCardBalance.acnRmd),
    //   })
    // );

    const balanceRequestArr = [
      CardService.getCardBalance(cardNumber),
      CardService.getCardBalance(toCardNo),
    ];

    Promise.allSettled(balanceRequestArr)
      .then((result) => {
        result.forEach(async (val, index) => {
          if (val.status === "rejected") {
            await balanceRequestArr[index];
          }
        });
        // Redux에 저장된 balance를 update
        result.forEach((val, index) => {
          if (val.status === "fulfilled") {
            const acnRmd = val.value.data?.acnRmd;
            if (acnRmd) {
              if (index === 0) {
                dispatch(
                  updateCard({
                    cardNumber: cardNumber,
                    balance: parseInt(acnRmd),
                  })
                );
              } else if (index === 1) {
                dispatch(
                  updateCard({
                    cardNumber: toCardNo,
                    balance: parseInt(acnRmd),
                  })
                );
              }
            }
          }
        });
        // TODO: success modal
        alert("Transfer success");
        dispatch(resetCard());
        window.location.reload();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setBalanceToTransfer("0");
        setIsLoading(false);
      });
  };

  return (
    <CardLayout className="border-none p-2 shadow-none">
      <div className="w-full space-y-4">
        <label>
          <p className="mb-2 font-bold">
            <FormattedMessage id="card.transferAmount" />
          </p>
          <TextField
            fullWidth
            name="balance"
            type={isFocused ? "number" : "text"}
            value={balanceToTransfer}
            onChange={handleChangeAmount}
            InputProps={{
              inputProps: {
                min: 0,
              },
              sx: {
                height: "48px",

                "& .MuiInputBase-input": {
                  textAlign: "end",
                },
                "& input[type=number]": {
                  "-moz-appearance": "textfield",
                  margin: 0.3,
                },
                "& input[type=number]::-webkit-outer-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 0.3,
                },
                "& input[type=number]::-webkit-inner-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 0.3,
                },
                "& input[type=text]": {
                  "-moz-appearance": "textfield",
                  margin: 1,
                },
                "& input[type=text]::-webkit-outer-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 1,
                },
                "& input[type=text]::-webkit-inner-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 1,
                },
              },
              endAdornment: intl.formatMessage({ id: "payment.koreanWon" }),
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="0"
          />
        </label>
        <div className="w-full space-y-2">
          <div className="w-full space-y-2">
            <p className="font-bold">
              <FormattedMessage id="card.transferFrom" />
            </p>

            <FormControl>
              <RadioGroup value={cardNumber} name="from-card">
                <FormControlLabel
                  value={cardNumber}
                  disabled
                  control={<Radio />}
                  label={<CardThumbnail cardName={cardNumber} />}
                />
              </RadioGroup>
            </FormControl>
          </div>
          <Divider flexItem />
          <div className="w-full space-y-2">
            <p className="font-bold">
              <FormattedMessage id="card.transferTo" />
            </p>
            <FormControl>
              <RadioGroup
                // row
                // aria-labelledby="demo-radio-buttons-group-label"
                value={toCardNo}
                name="to-cards-group"
                onChange={handleChangeToCard}
              >
                {toCards.map((card) => (
                  <FormControlLabel
                    key={card.iapCdno}
                    value={card.iapCdno}
                    control={<Radio />}
                    label={
                      <CardThumbnail
                        key={card.iapCdno}
                        cardName={card.iapCdno}
                      />
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </div>
        </div>

        <CustomButton
          size="medium"
          variant="outlined"
          onClick={transferBalance}
          disabled={!isActive || balanceToTransfer === "0"}
          id="card.transfer"
        />
      </div>
      {isLoading && <LoadingSpinner overlap />}
    </CardLayout>
  );
};

export default Transfer;

//
const CardThumbnail = ({ cardName }: { cardName: string }) => {
  return (
    <div className="flex items-center gap-2">
      <CreditCardIcon />
      <p className="text-sm font-bold">{formatCardNumber(cardName)}</p>
    </div>
  );
};
